import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';
import { useDocumentTitle } from '../lib/useDocumentTitle';

// Plain-language privacy policy. Mirror anything material in this file in
// the Terms of Service + the Profile page's "manage your data" section.
// Effective date is hand-maintained — bump it whenever wording changes.

const EFFECTIVE_DATE = '2026-05-29';
const CONTACT = 'aitookmyjobagain@gmail.com';

const PrivacyPage: React.FC = () => {
  useDocumentTitle('privacy policy');
  return (
    <div className="legal-page">
      <Link to="/" className="legal-back">← back to study buddy</Link>
      <h1>Privacy Policy</h1>
      <div className="legal-meta">Effective {EFFECTIVE_DATE}</div>

      <p>
        Study Buddy is a free coding-interview prep tool. We collect the
        minimum information needed to run the service, and we don't sell
        or share your data with advertisers. This page describes what we
        collect, why, and how to delete it.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li>
          <strong>Email address.</strong> Required to create an account.
          Stored by AWS Cognito. Used only to verify the account and to
          contact you about your account (e.g. security notices).
        </li>
        <li>
          <strong>Handle.</strong> A username you pick during signup.
          Public — other users see it on the leaderboard, profile pages,
          and friend lists.
        </li>
        <li>
          <strong>Solve records.</strong> Which problems you've solved,
          when, how long it took, which language you used, and how many
          hints you used. Used to track your progress, drive the
          leaderboard, and award XP/coins/trophies.
        </li>
        <li>
          <strong>Friends list.</strong> The users you've added as
          friends. Visible to those friends.
        </li>
        <li>
          <strong>In-progress code.</strong> Stored in your browser's
          local storage. Never sent to our servers. Cleared when you
          clear your browser data.
        </li>
        <li>
          <strong>Usage events.</strong> Which pages you visit, which
          problems you open, how long you spend on a problem, and
          whether you solved it. We use this to understand which
          features and problems are working. The events are stored in
          our own AWS CloudWatch logs in the same us-east-1 region as
          your account data — no third-party analytics service, no
          advertising trackers, no cross-site cookies. Retention is
          capped at 90 days.
        </li>
        <li>
          <strong>Google sign-in (optional).</strong> If you sign in with
          Google, Google shares your email address with us. We don't
          receive your Google contacts, calendar, or any other Google data.
        </li>
      </ul>

      <h2>What we don't collect</h2>
      <ul>
        <li>We don't run any third-party advertising or tracking pixels.</li>
        <li>We don't use cross-site cookies.</li>
        <li>We don't sell or share user data with third parties.</li>
        <li>
          We don't track which code you write — code stays in your
          browser. Only the fact that you solved a problem reaches the
          server.
        </li>
      </ul>

      <h2>How we use it</h2>
      <p>
        Your data is used to run the product: authenticate you, save
        your progress so it follows you across devices, populate
        leaderboards, deliver friend notifications, and award progression
        rewards. We may use aggregate, anonymous statistics (e.g. "how
        many users solved problem X this week") to improve the service.
      </p>

      <h2>Where it lives</h2>
      <p>
        All data is stored on Amazon Web Services in the us-east-1
        region. Authentication data lives in AWS Cognito; everything
        else lives in AWS DynamoDB and Amazon S3. We don't have copies
        of your data anywhere else.
      </p>

      <h2>Your rights</h2>
      <p>You can, at any time:</p>
      <ul>
        <li>
          <strong>See your data</strong> — download a JSON copy of
          everything we have about you from your profile page
          ("Export my data"). This includes your summary, your solve
          history, your friends list, and your trophy progress.
        </li>
        <li>
          <strong>Delete your data</strong> — close your account from
          your profile page ("Delete my account"). This permanently
          wipes your email, handle, solve records, friend relationships,
          and Cognito account. Anonymized leaderboard entries may be
          retained briefly (≤30 days) before being cleaned up.
        </li>
        <li>
          <strong>Sign out from a device</strong> — clears the session
          tokens stored in your browser without deleting your account.
        </li>
      </ul>

      <h2>Children's privacy</h2>
      <p>
        Study Buddy isn't directed at children under 13. If you're a
        parent and you believe your child created an account, contact us
        at <a href={`mailto:${CONTACT}`}>{CONTACT}</a> and we'll
        delete it.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        We'll update the effective date at the top of this page if we
        change anything material. For non-trivial changes (e.g. adding a
        new category of collected data) we'll also notify signed-in
        users by email.
      </p>

      <h2>Contact</h2>
      <p>
        Questions about your data or this policy? Email{' '}
        <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.
      </p>
    </div>
  );
};

export default PrivacyPage;
